import React, { useCallback, useEffect, useState, useRef, useContext, useMemo } from "react";
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { MultiSelect } from "primereact/multiselect";
import { Paginator } from "primereact/paginator";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Dropdown } from "primereact/dropdown";
import { BreadCrumb } from "primereact/breadcrumb";
import { ProgressSpinner } from "primereact/progressspinner";
import { getLanguages, createLanguage, updateLanguage, toggleLanguage, deleteLanguages } from "../../api/languageApi";
import { getCountries } from "../../api/countryApi";
import { AuthContext } from "../../Context/AuthContext";
import LanguageForm from "./LanguageForm";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

export default function Language() {
    const [languages, setLanguages] = useState([]);
    const [allCountries, setAllCountries] = useState([]);
    const [selected, setSelected] = useState([]);
    const [formVisible, setFormVisible] = useState(false);
    const [editData, setEditData] = useState(null);
    const [first, setFirst] = useState(0);
    const [rows, setRows] = useState(10);
    const [filterValue, setFilterValue] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    const toast = useRef(null);
    const { auth } = useContext(AuthContext);
    const token = auth?.token;

    const items = [{ label: "LANGUAGE" }];
    const home = { icon: "pi pi-home", url: "/" };

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const [langRes, countryRes] = await Promise.all([
                getLanguages(token),
                getCountries(token),
            ]);

            const languagesData = langRes.data.data;
            const countriesData = countryRes.data.data;
            const isClient = auth?.role === "client";

            const mapped = languagesData
                .filter(l => !isClient || l.is_enabled)
                .map(l => ({
                    ...l,
                    Countries: countriesData.filter(c =>
                        c.is_enabled && l.Countries?.some(lc => lc.id === c.id)
                    )
                }));

            setLanguages(mapped);
            setAllCountries(countriesData);
        } catch {
            toast.current?.show({ severity: "error", summary: "Error", detail: "Failed to fetch data" });
        } finally {
            setIsLoading(false);
        }
    }, [token, auth?.role]);

    useEffect(() => {
        if (token) fetchData();
    }, [token, fetchData]);

    const textEditor = options => (
        <InputText
            value={options.value}
            onChange={e => options.editorCallback(e.target.value)}
            disabled={!options.rowData.is_enabled}
        />
    );

    const countryEditor = options => {
    const { rowData, value, editorCallback } = options;

    const selectedIds = Array.isArray(value)
        ? value.map(v => (typeof v === "object" ? v.id : v))
        : rowData.Countries?.map(c => c.id) || [];

    return (
        <MultiSelect
            value={selectedIds}
            options={allCountries.map(c => ({ label: c.name, value: c.id }))}
            onChange={e =>
                editorCallback(
                    allCountries.filter(c => e.value.includes(c.id))
                )
            }
            display="chip"
            placeholder="Select Country"
            disabled={!rowData.is_enabled}
            className="w-full"
        />
    );
};

    const onCellEditComplete = e => {
        if (!e.rowData.is_enabled) return;
        const { rowData, newValue, field } = e;
        if ((field !== "Countries" && !newValue) || (field !== "Countries" && newValue === "")) return;

        let formattedValue = newValue;
        if (field === "code") {
           formattedValue = newValue.toLowerCase();
        } else if (field === "name") {
          formattedValue = newValue.charAt(0).toUpperCase() + newValue.slice(1).toLowerCase();
        }
        confirmDialog({
            message: "Are you sure you want to update this language?",
            header: "Confirm Update",
            icon: "pi pi-exclamation-triangle",
            accept: async () => {
                setIsLoading(true);
                try {
                    await updateLanguage(
                        rowData.id,
                        {
                            code: field === "code" ? formattedValue : rowData.code,
                            name: field === "name" ? formattedValue : rowData.name,
                            countryIds:
                                field === "Countries"
                                    ? (newValue?.map(c => c.id) || [])
                                    : rowData.Countries.map(c => c.id)
                        },
                        token
                    );
                    fetchData();
                    toast.current.show({ severity: "success", summary: "Updated", detail: "Language updated" });
                } catch (err) {
                    toast.current.show({
                        severity: "error",
                        summary: "Error",
                        detail: err.response?.data?.message || "Update failed"
                    });
                } finally {
                    setIsLoading(false);
                }
            }
        });
    };

    const confirmToggle = ids => {
        if (!ids.length) return;
        confirmDialog({
            message: "Are you sure you want to toggle status of selected language(s)?",
            header: "Confirm Status Toggle",
            icon: "pi pi-exclamation-triangle",
            accept: async () => {
                setIsLoading(true);
                try {
                    for (const id of ids) await toggleLanguage(id, token);
                    fetchData();
                    setSelected([]);
                    toast.current.show({ severity: "success", summary: "Success", detail: "Status updated" });
                } catch (err) {
                    toast.current.show({
                        severity: "error",
                        summary: "Error",
                        detail: err.response?.data?.message || "Operation failed"
                    });
                } finally {
                    setIsLoading(false);
                }
            }
        });
    };

    const confirmDelete = (ids) => {
    if (!ids.length) return;

    confirmDialog({
        message: "Are you sure you want to delete the selected language(s)?",
        header: "Confirm Delete",
        icon: "pi pi-trash",
        accept: async () => {
            setIsLoading(true);
            try {
                await deleteLanguages(ids, token);

                await fetchData();
                setSelected([]);
                toast.current.show({
                    severity: "success",
                    summary: "Deleted",
                    detail: "Language(s) deleted successfully"
                });
            } catch (err) {
                toast.current.show({
                    severity: "error",
                    summary: "Error",
                    detail: err.response?.data?.message || err.message || "Delete failed"
                });
            } finally {
                setIsLoading(false);
            }
        }
    });
};


    const filteredLanguages = useMemo(() => {
        if (!filterValue) return languages;
        return languages.filter(l =>
            `${l.code} ${l.name}`.toLowerCase().includes(filterValue.toLowerCase())
        );
    }, [languages, filterValue]);

    const languageFilterElement = () => {
        const options = languages.map(l => ({
            label: `${l.code} - ${l.name}`,
            value: `${l.code} ${l.name}`
        }));
        return (
            <Dropdown
                value={filterValue}
                options={options}
                editable
                placeholder="Search Language"
                onChange={e => setFilterValue(e.value || "")}
                panelStyle={{ maxWidth: "220px" }}
            />
        );
    };

    const filterEnabledLanguages = data => data.filter(l => l.is_enabled);

    const downloadJSON = () => {
        const data = selected.length ? selected : filterEnabledLanguages(languages);
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = "languages.json";
        link.click();
    };

    const downloadExcel = () => {
        const data = selected.length ? selected : filterEnabledLanguages(languages);
        const worksheet = XLSX.utils.json_to_sheet(
            data.map(d => ({
                code: d.code,
                name: d.name,
                countries: d.Countries?.map(c => c.name).join(", ") || ""
            }))
        );
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Languages");
        XLSX.writeFile(workbook, "languages.xlsx");
    };

    const downloadPDF = () => {
        const data = selected.length ? selected : filterEnabledLanguages(languages);
        const doc = new jsPDF();
        doc.text("Languages", 14, 20);
        const rows = data.map(d => [
            d.code,
            d.name,
            d.Countries?.map(c => c.name).join(", ") || ""
        ]);
        autoTable(doc, { head: [["Code", "Name", "Countries"]], body: rows, startY: 30 });
        doc.save("languages.pdf");
    };

    const renderCountries = row => (
        <div className="flex flex-wrap gap-1">
            {row.Countries?.map(c => (
                <span
                    key={c.id}
                    style={{
                        backgroundColor: "#FC5F1CFF",
                        color: "#fff",
                        padding: "2px 8px",
                        borderRadius: "12px",
                        fontSize: "0.85rem"
                    }}
                >
                    {c.name}
                </span>
            ))}
        </div>
    );

    const handleFormSubmit = async (data) => {
        setIsLoading(true);
        try {
            if (editData) {
                await updateLanguage(editData.id, data, token);
            } else {
                await createLanguage(data, token);
            }
            setFormVisible(false);
            setEditData(null);
            await fetchData();
            toast.current?.show({ severity: "success", summary: "Success", detail: "Operation completed" });
        }
        catch (err) {
            toast.current?.show({ severity: "error", summary: "Error", detail: err?.response?.data?.message || "Operation failed" });
        } finally {
            setIsLoading(false);
        }
    };

    const rowClassName = rowData => ({
        "opacity-50": !rowData.is_enabled,
        "cursor-not-allowed": !rowData.is_enabled
    });

    return (
        <div className="p-4" style={{ position: 'relative' }}>
            {isLoading && (
                <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(255,255,255,0.8)', zIndex: 10, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <ProgressSpinner />
                </div>
            )}
            <Toast ref={toast} />
            <ConfirmDialog />
            <h2 className="mb-1 text-3xl font-bold" style={{ color: "#FC5F1CFF" }}>LANGUAGE</h2>
            <div className="gap-2 mb-4">
                <BreadCrumb model={items} home={home} style={{ background: "transparent", border: "none" }} />
            </div>
            <DataTable
                value={filteredLanguages.slice(first, first + rows)}
                dataKey="id"
                selection={selected}
                onSelectionChange={e => setSelected(e.value)}
                editMode="cell"
                showGridlines
                rowClassName={rowClassName}
                header={
                    <div className="flex justify-content-end gap-2">
                        <Button 
                            icon="pi pi-plus" 
                            tooltip="add"
                            rounded onClick={() => setFormVisible(true)} 
                            style={{ background: "#FC5F1CFF", border: "none" }} 
                        />
                        <Button 
                            icon="pi pi-file" 
                            rounded 
                            tooltip="json"
                            onClick={downloadJSON} 
                            style={{ background: "#FC5F1CFF", border: "none" }} 
                            />
                        <Button 
                            icon="pi pi-file-excel" 
                            rounded 
                            tooltip="xlsx"
                            onClick={downloadExcel} 
                            style={{ background: "#FC5F1CFF", border: "none" }} 
                        />
                        <Button 
                            icon="pi pi-file-pdf" 
                            rounded 
                            tooltip="pdf"
                            onClick={downloadPDF} 
                            style={{ background: "#FC5F1CFF", border: "none" }} 
                        />
                        {/* <Button 
                            icon="pi pi-ban" 
                            rounded 
                            disabled={!selected.length} 
                            onClick={() => confirmToggle(selected.map(s => s.id))} 
                            style={{ background: "#FC5F1CFF", border: "none" }} 
                        /> */}
                        <Button 
                            icon="pi pi-ban"
                            rounded 
                            tooltip="disable"
                            disabled={!selected.length} 
                            onClick={() => confirmToggle(selected.map(p => p.id))} 
                            style={{ background: "#FC5F1CFF", border: "none" }}
                        />
                        <Button 
                            icon="pi pi-trash" 
                            rounded 
                            tooltip="delete"
                            disabled={!selected.length} 
                            onClick={() => confirmDelete(selected.map(s => s.id))} 
                            style={{ background: "#FF4B4B", border: "none", color: "#fff" }} 
                        />
                    </div>
                }
                footer={
                    <Paginator
                        first={first}
                        rows={rows}
                        totalRecords={filteredLanguages.length}   
                        rowsPerPageOptions={[10, 20, 30]}
                        onPageChange={e => { setFirst(e.first); setRows(e.rows); }}
                    />
                }
            >
                <Column selectionMode="multiple" style={{ width: "3rem" }} />
                <Column
                    field="code"
                    header="Code"
                    editor={textEditor}
                    onCellEditComplete={onCellEditComplete}
                    filter
                    filterElement={languageFilterElement}
                />
                <Column
                    field="name"
                    header="Name"
                    editor={textEditor}
                    onCellEditComplete={onCellEditComplete}
                    filter
                    filterElement={languageFilterElement}
                />
                <Column
                    field="Countries"
                    header="Countries"
                    editor={countryEditor}
                    onCellEditComplete={onCellEditComplete}
                    body={renderCountries}
                />
            </DataTable>
            <LanguageForm
                visible={formVisible}
                onHide={() => { setFormVisible(false); setEditData(null); }}
                initialData={editData}
                onSubmit={handleFormSubmit}
                countries={allCountries}
            />
        </div>
    );
}
